import { consumePostLoginReturnPath } from './postLoginReturnPath';

export const AUTH_ROUTES = {
  login: '/login',
  setup2fa: '/setup-2fa',
  updateAuth: '/update-auth',
  dashboard: '/dashboard',
};

/**
 * Shared destination resolver for authenticated sessions.
 * @param {{ needsSetup2FA?: boolean, needsPasswordUpdate?: boolean, isSessionComplete?: boolean }} session
 * @param {{ consumeReturnPath?: boolean }} options
 */
function resolveAuthenticatedDestination(session, { consumeReturnPath = false } = {}) {
  if (session.needsSetup2FA) {
    return AUTH_ROUTES.setup2fa;
  }

  if (session.needsPasswordUpdate) {
    return AUTH_ROUTES.updateAuth;
  }

  if (session.isSessionComplete) {
    if (consumeReturnPath) {
      return consumePostLoginReturnPath() || AUTH_ROUTES.dashboard;
    }
    return AUTH_ROUTES.dashboard;
  }

  return null;
}

/**
 * Entry redirect for `/` — never consumes a stored post-login return path.
 */
export function getRootRedirect(session) {
  if (!session.isAuthenticated || session.isPendingLogin2FA) {
    return AUTH_ROUTES.login;
  }

  return resolveAuthenticatedDestination(session) || AUTH_ROUTES.login;
}

/**
 * Where to send an authenticated user leaving guest-only routes (e.g. /login).
 * Returns null when the current page should stay mounted (login OTP step).
 */
export function getGuestRouteRedirect(session) {
  if (!session.isAuthenticated || session.isPendingLogin2FA) {
    return null;
  }

  return resolveAuthenticatedDestination(session, { consumeReturnPath: true });
}

/**
 * Redirect target for onboarding routes (/setup-2fa, /update-auth).
 * Returns null when the user is on the correct partial-session page.
 */
export function getOnboardingRouteRedirect(session, flow) {
  if (!session.isAuthenticated) {
    return AUTH_ROUTES.login;
  }

  if (session.isSessionComplete) {
    return resolveAuthenticatedDestination(session, { consumeReturnPath: true });
  }

  if (flow === 'setup2fa') {
    if (session.needsPasswordUpdate) {
      return AUTH_ROUTES.updateAuth;
    }
    if (!session.needsSetup2FA) {
      return null;
    }
    return null;
  }

  if (flow === 'updatePassword') {
    if (session.needsSetup2FA) {
      return AUTH_ROUTES.setup2fa;
    }
    if (!session.needsPasswordUpdate) {
      return null;
    }
    return null;
  }

  return null;
}

export function shouldRenderGuestChildren(session) {
  if (!session.isAuthenticated) {
    return true;
  }

  if (session.isPendingLogin2FA) {
    return true;
  }

  if (session.needsSetup2FA || session.needsPasswordUpdate) {
    return false;
  }

  return !session.isSessionComplete;
}

export function shouldRenderOnboardingChildren(session, flow) {
  if (!session.isAuthenticated || session.isSessionComplete) {
    return false;
  }

  if (flow === 'setup2fa') {
    return session.needsSetup2FA;
  }

  if (flow === 'updatePassword') {
    return session.needsPasswordUpdate;
  }

  return false;
}
